class ProductSlider extends HTMLElement {
  productId = null;
  swiper = null;
  variants = [];
  variantChangeUnsubscriber = undefined;
  optionChangeUnsubscriber = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.variants = this.getVariants();
    this.initSlider();
    this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onOptionChange.bind(this));
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
  }

  disconnectedCallback() {
    this.optionChangeUnsubscriber?.();
    this.variantChangeUnsubscriber?.();
    this.swiper?.destroy(true);
  }

  getSectionId() {
    const productInfo = this.closest('product-info');
    return productInfo?.dataset.originalSection || productInfo?.dataset.section;
  }

  getVariants() {
    const variantJson = this.querySelector('[data-product-variants]')?.textContent;
    if (!variantJson) return [];

    try {
      return JSON.parse(variantJson);
    } catch {
      return [];
    }
  }

  getSelectedVariant() {
    const variantJson = this.closest('product-info')?.querySelector('[data-selected-variant]')?.textContent;

    if (!variantJson) return null;

    try {
      return JSON.parse(variantJson);
    } catch {
      return null;
    }
  }

  getSelectedOptionValues() {
    const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
    if (!variantSelects) return [];

    return Array.from(variantSelects.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (element) => element.value
    );
  }

  findVariantBySelectedOptions() {
    const selectedOptions = this.getSelectedOptionValues();
    if (!selectedOptions.length) return null;

    return this.variants.find((variant) =>
      selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
    );
  }

  getSlideIndexForVariant(variant) {
    if (!variant?.featured_media?.id) return 0;

    const mediaId = String(variant.featured_media.id);
    const slides = Array.from(this.querySelectorAll('.swiper-slide[data-media-id]'));
    const slideIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);

    return slideIndex >= 0 ? slideIndex : 0;
  }

  onOptionChange(event) {
    const productInfo = this.closest('product-info');
    if (!productInfo?.contains(event.data.event.target)) return;

    const variant = this.findVariantBySelectedOptions();
    if (variant) this.updateSlideForVariant(variant, true);
  }

  onVariantChange(event) {
    if (event.data.sectionId !== this.getSectionId()) return;
    this.updateSlideForVariant(event.data.variant, true);
  }

  updateSlideForVariant(variant, instant = true) {
    if (!variant?.featured_media?.id || !this.swiper) return;

    if (this.dataset.metafieldOnly === 'true') {
      this.updateMetafieldImage(variant);
      return;
    }

    const slideIndex = this.getSlideIndexForVariant(variant);

    if (slideIndex >= 0) {
      this.swiper.slideTo(slideIndex, instant ? 0 : this.swiper.params.speed);
      this.syncSliderHeight(instant);
      this.hideInactiveSlides();
    }
  }

  updateMetafieldImage(variant) {
    const image = this.querySelector('.product__image--slider img');
    const previewImage = variant.featured_media?.preview_image;

    if (!image || !previewImage?.src) return;

    image.src = previewImage.src;

    if (previewImage.srcset) {
      image.srcset = previewImage.srcset;
    } else {
      image.removeAttribute('srcset');
    }

    if (previewImage.alt) {
      image.alt = previewImage.alt;
    }

    this.syncSliderHeight(true);
  }

  syncSliderHeight(instant = false) {
    if (!this.swiper) return;

    this.swiper.updateAutoHeight(instant ? 0 : 150);

    const sliderEl = this.querySelector('.product__image--slider');
    const activeSlide = sliderEl?.querySelector('.swiper-slide-active');

    if (!sliderEl || !activeSlide) return;

    const setHeight = () => {
      sliderEl.style.height = `${activeSlide.offsetHeight}px`;
    };

    const activeImage = activeSlide.querySelector('img');

    if (activeImage?.complete) {
      setHeight();
    } else {
      activeImage?.addEventListener('load', setHeight, { once: true });
    }
  }

  hideInactiveSlides() {
    this.querySelectorAll('.swiper-slide').forEach((slide) => {
      const isActive = slide.classList.contains('swiper-slide-active');
      slide.style.visibility = isActive ? '' : 'hidden';
      slide.style.pointerEvents = isActive ? '' : 'none';
    });
  }

  initSlider() {
    const sliderEl = this.querySelector('.product__image--slider');
    const nextEl = this.querySelector('.swiper-button-next-custom');
    const prevEl = this.querySelector('.swiper-button-prev-custom');
    const initialVariant = this.getSelectedVariant();
    const initialSlide = this.getSlideIndexForVariant(initialVariant);

    this.swiper = new Swiper(sliderEl, {
      slidesPerView: 1,
      loop: false,
      autoHeight: true,
      initialSlide,
      autoplay: false,
      speed: 300,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      on: {
        init: () => {
          if (this.dataset.metafieldOnly === 'true' && initialVariant?.featured_media) {
            this.updateMetafieldImage(initialVariant);
          }

          this.syncSliderHeight(true);
          this.hideInactiveSlides();
        },
        slideChangeTransitionEnd: () => {
          this.syncSliderHeight(false);
          this.hideInactiveSlides();
        },
      },
    });
  }
}

if (!customElements.get('product-slider')) {
  customElements.define('product-slider', ProductSlider);
}
